import * as z from 'zod'

import { type Region, regionSchema } from '#dictionary/regions'

import raw from './realEstateRates.json'

export const realEstateRateSchema = z
  .object({
    year: z.int(),
    region: regionSchema,
    growthRate: z.number(),
    taxRate: z.number().min(0).max(1),
  })
  .strict()

export type RealEstateRate = z.infer<typeof realEstateRateSchema>

// Year-ASC order is load-bearing — `LATEST_RATE_BY_REGION` below builds by
// iterating and overwriting per region, so the last row wins.
export const REAL_ESTATE_RATES: readonly RealEstateRate[] = z
  .array(realEstateRateSchema)
  .parse(raw)
  .sort((a, b) => a.region.localeCompare(b.region) || a.year - b.year)

const buildRateByRegionYear = (): ReadonlyMap<
  Region,
  ReadonlyMap<number, RealEstateRate>
> => {
  const outer = new Map<Region, Map<number, RealEstateRate>>()
  for (const rate of REAL_ESTATE_RATES) {
    let inner = outer.get(rate.region)
    if (!inner) {
      inner = new Map()
      outer.set(rate.region, inner)
    }
    inner.set(rate.year, rate)
  }
  return outer
}

export const RATE_BY_REGION_YEAR = buildRateByRegionYear()

const buildLatestRateByRegion = (): ReadonlyMap<Region, RealEstateRate> => {
  const latest = new Map<Region, RealEstateRate>()
  for (const rate of REAL_ESTATE_RATES) {
    latest.set(rate.region, rate)
  }
  return latest
}

export const LATEST_RATE_BY_REGION = buildLatestRateByRegion()

export const getRateForRegionYear = (
  region: Region,
  year: number
): RealEstateRate | undefined => {
  const yearMap = RATE_BY_REGION_YEAR.get(region)
  if (!yearMap) return undefined

  const exact = yearMap.get(year)
  if (exact) return exact

  let closest: RealEstateRate | undefined
  let closestDiff = Infinity
  for (const rate of yearMap.values()) {
    const diff = Math.abs(rate.year - year)
    if (diff < closestDiff) {
      closest = rate
      closestDiff = diff
    }
  }
  return closest
}
